import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import SearchIcon from '@material-ui/icons/Search'
import { autocompleteCity } from '@lib/geo-api'
import { resolveLocationOptionFromInput } from '@lib/pga-coach/resolve-journey-location'
import { MapPin, COACH_PROFILE_FILTER_ICON_PROPS } from '@lib/pga-coach/coach-profile-icons'
import { useStyles } from '../CoachesSearch.styles'

const toResolveOption = (option) => ({
  label: option.label,
  placeName: option.label,
  aroundLatLng: `${option.lat}, ${option.lng}`,
})

function NearLocationInput({ defaultPlaceName = '', onLocationSelect }) {
  const classes = useStyles()
  const [inputValue, setInputValue] = useState(defaultPlaceName || '')
  const [options, setOptions] = useState([])

  useEffect(() => {
    setInputValue(defaultPlaceName || '')
  }, [defaultPlaceName])

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([])
      return undefined
    }

    let cancelled = false
    const load = async () => {
      const cities = await autocompleteCity(inputValue)
      if (cancelled) return
      setOptions(cities.map((item) => ({
        id: item.objectID,
        label: `${item.city}, ${item.state}`,
        lat: item._geoloc?.lat,
        lng: item._geoloc?.lng,
      })))
    }
    load()
    return () => { cancelled = true }
  }, [inputValue])

  const applyResolved = (resolved) => {
    if (!resolved?.aroundLatLng || typeof onLocationSelect !== 'function') return
    setInputValue(resolved.label)
    onLocationSelect(resolved.aroundLatLng, resolved.placeName || resolved.label)
  }

  const handleSelect = (option) => {
    if (!option?.lat || !option?.lng || typeof onLocationSelect !== 'function') return
    applyResolved(toResolveOption(option))
  }

  const applyTypedLocation = async (raw = inputValue) => {
    const resolved = await resolveLocationOptionFromInput(
      raw,
      options.filter((option) => option.lat && option.lng).map(toResolveOption),
    )
    applyResolved(resolved)
  }

  return (
    <Box className={classes.splitLocationCluster}>
      <Autocomplete
        freeSolo
        aria-label="City or zip"
        className={classes.splitNearAutocomplete}
        classes={{
          paper: classes.contextAutocompletePaper,
          option: classes.contextAutocompleteOption,
        }}
        filterOptions={(value) => value}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        inputValue={inputValue}
        forcePopupIcon={false}
        openOnFocus
        onInputChange={(event, value, reason) => {
          if (reason === 'reset') return
          setInputValue(value)
        }}
        onChange={(event, option) => {
          if (typeof option === 'string') {
            applyTypedLocation(option)
            return
          }
          handleSelect(option)
        }}
        renderInput={(params) => (
          <TextField
            {...{
              ...params,
              inputProps: {
                ...params.inputProps,
                autoComplete: 'off',
                name: 'coach-directory-location',
                'aria-label': 'City or zip',
                'data-1p-ignore': true,
                'data-lpignore': 'true',
                'data-bwignore': true,
                'data-form-type': 'other',
              },
            }}
            variant="outlined"
            placeholder="Enter your city or zip"
            size="small"
            fullWidth
            autoComplete="off"
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              applyTypedLocation(event.target.value)
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <MapPin
                    {...COACH_PROFILE_FILTER_ICON_PROPS}
                    size={18}
                    className={classes.contextLocationIcon}
                  />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <IconButton
        type="button"
        className={classes.splitSearchButton}
        aria-label="Search coaches by location"
        onClick={() => applyTypedLocation(inputValue)}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

NearLocationInput.propTypes = {
  defaultPlaceName: PropTypes.string,
  onLocationSelect: PropTypes.func,
}

export default NearLocationInput
